<template>
  <v-card variant="outlined" class="mb-4">
    <v-toolbar color="deep-orange" density="compact">
      <v-icon class="ml-4">mdi-radio-tower</v-icon>
      <v-toolbar-title>Roaming</v-toolbar-title>
      <v-chip v-if="roamingActive" variant="flat" color="white" size="small" class="mr-2">
        Zona {{ currentRoamingZone }} activa
      </v-chip>
      <v-chip v-else variant="flat" color="white" size="small" class="mr-2">
        Inactiv
      </v-chip>
    </v-toolbar>

    <v-card-text>
      <v-alert v-if="mvnoPending" type="warning" density="compact" class="mb-3">
        <v-icon start>mdi-clock-outline</v-icon>
        SIM-ul asteapta confirmarea unei comenzi MVNO. Butoanele sunt blocate.
      </v-alert>

      <v-row>
        <!-- Zone 1 -->
        <v-col cols="12" md="6">
          <v-card
            :variant="currentRoamingZone === 1 ? 'elevated' : 'outlined'"
            :color="currentRoamingZone === 1 ? 'deep-orange-lighten-5' : undefined"
            :class="{ 'border-deep-orange': currentRoamingZone === 1 }"
            class="pa-4"
          >
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="text-h6 font-weight-bold">
                <v-icon start color="deep-orange">mdi-earth</v-icon>
                Zona 1
              </div>
              <v-chip v-if="currentRoamingZone === 1" color="deep-orange" variant="flat" size="small">
                <v-icon start size="small">mdi-check-circle</v-icon>
                ACTIV
              </v-chip>
            </div>

            <!-- Countries list (expandable) -->
            <v-expand-transition>
              <div v-if="expanded1">
                <v-divider class="mb-2" />
                <div class="text-caption text-grey-darken-1 mb-2">Tari incluse in Zona 1:</div>
                <div class="text-body-2 mb-3" style="white-space: pre-line;">{{ countriesZone1 || 'Nu sunt configurate tarile' }}</div>
              </div>
            </v-expand-transition>

            <div class="d-flex gap-2">
              <v-btn
                size="small"
                variant="text"
                @click="expanded1 = !expanded1"
              >
                <v-icon start size="small">{{ expanded1 ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                {{ expanded1 ? 'Ascunde tari' : 'Vezi tari' }}
              </v-btn>

              <v-spacer />

              <v-btn
                v-if="currentRoamingZone === 1"
                color="red"
                variant="flat"
                size="small"
                :loading="changingZone === 1"
                :disabled="mvnoPending || changingZone !== null"
                @click="changeRoaming(1)"
              >
                <v-icon start size="small">mdi-close-circle</v-icon>
                Dezactiveaza Roaming Zona 1
              </v-btn>
              <v-btn
                v-else
                color="deep-orange"
                variant="flat"
                size="small"
                :loading="changingZone === 1"
                :disabled="mvnoPending || changingZone !== null"
                @click="changeRoaming(1)"
              >
                <v-icon start size="small">mdi-check-circle</v-icon>
                Activeaza Roaming Zona 1
              </v-btn>
            </div>
          </v-card>
        </v-col>

        <!-- Zone 2 -->
        <v-col cols="12" md="6">
          <v-card
            :variant="currentRoamingZone === 2 ? 'elevated' : 'outlined'"
            :color="currentRoamingZone === 2 ? 'deep-orange-lighten-5' : undefined"
            :class="{ 'border-deep-orange': currentRoamingZone === 2 }"
            class="pa-4"
          >
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="text-h6 font-weight-bold">
                <v-icon start color="deep-orange">mdi-earth</v-icon>
                Zona 2
              </div>
              <v-chip v-if="currentRoamingZone === 2" color="deep-orange" variant="flat" size="small">
                <v-icon start size="small">mdi-check-circle</v-icon>
                ACTIV
              </v-chip>
            </div>

            <!-- Countries list (expandable) -->
            <v-expand-transition>
              <div v-if="expanded2">
                <v-divider class="mb-2" />
                <div class="text-caption text-grey-darken-1 mb-2">Tari incluse in Zona 2:</div>
                <div class="text-body-2 mb-3" style="white-space: pre-line;">{{ countriesZone2 || 'Nu sunt configurate tarile' }}</div>
              </div>
            </v-expand-transition>

            <div class="d-flex gap-2">
              <v-btn
                size="small"
                variant="text"
                @click="expanded2 = !expanded2"
              >
                <v-icon start size="small">{{ expanded2 ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                {{ expanded2 ? 'Ascunde tari' : 'Vezi tari' }}
              </v-btn>

              <v-spacer />

              <v-btn
                v-if="currentRoamingZone === 2"
                color="red"
                variant="flat"
                size="small"
                :loading="changingZone === 2"
                :disabled="mvnoPending || changingZone !== null"
                @click="changeRoaming(2)"
              >
                <v-icon start size="small">mdi-close-circle</v-icon>
                Dezactiveaza Roaming Zona 2
              </v-btn>
              <v-btn
                v-else
                color="deep-orange"
                variant="flat"
                size="small"
                :loading="changingZone === 2"
                :disabled="mvnoPending || changingZone !== null"
                @click="changeRoaming(2)"
              >
                <v-icon start size="small">mdi-check-circle</v-icon>
                Activeaza Roaming Zona 2
              </v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { defineComponent } from 'vue'
import { changeRoamingApi } from '@/composables/useNetworkServicesAPI'
import store from '@/store'

export default defineComponent({
  name: 'SimRoamingPanel',
  props: {
    simId: { type: [Number, String], required: true },
    roamingActive: { type: Boolean, default: false },
    currentRoamingZone: { type: Number, default: null },
    countriesZone1: { type: String, default: '' },
    countriesZone2: { type: String, default: '' },
    mvnoPending: { type: Boolean, default: false }
  },
  emits: ['roaming-changed'],
  data() {
    return {
      changingZone: null,
      expanded1: false,
      expanded2: false
    }
  },
  methods: {
    async changeRoaming(selectedZoneId) {
      this.changingZone = selectedZoneId
      const token = store.getters.getToken
      const result = await changeRoamingApi(token, this.simId, selectedZoneId)
      this.changingZone = null

      if (!result.error) {
        this.$emit('roaming-changed', { error: false, message: result.message })
      } else {
        this.$emit('roaming-changed', { error: true, message: result.message })
      }
    }
  }
})
</script>

<style scoped>
.border-deep-orange {
  border: 2px solid #FF5722 !important;
}
</style>
